import { View } from "@/components/ui";
import type { LineItemDetail } from "@/types/line-item";
import { LuceSparkle } from "@/components/shared/icons";
import { useMemo } from "react";
import { formatPrice } from "@/lib/helpers/number";
import {
  PackageDepartmentEnum,
  PackageServiceTypeEnum,
} from "@/__generated__/graphql";
import { Typography } from "../typography";

export function LineItemList({
  lineItems,
  withPrice,
  withUnit,
  mergedLineItems = true,
}: {
  lineItems: LineItemDetail[];
  withPrice?: boolean;
  withUnit?: boolean;
  mergedLineItems?: boolean;
}) {
  const mergedItems = useMemo(() => {
    if (!mergedLineItems) return lineItems;

    return Object.values(
      lineItems.reduce(
        (acc, item) => {
          const key = item.packageDetailId;
          if (!acc[key]) {
            acc[key] = { ...item };
          } else {
            acc[key].unitsNumber += item.unitsNumber || 0;
            acc[key].billingValue += item.billingValue || 0;
          }
          return acc;
        },
        {} as Record<string, (typeof lineItems)[0]>,
      ),
    );
  }, [lineItems, mergedLineItems]);

  const lineItemsGroup = useMemo(() => {
    const [firstItem] = lineItems;
    const shouldCombineItemValue =
      firstItem &&
      [
        PackageDepartmentEnum.CarpetUpholstery,
        PackageDepartmentEnum.HomeBeauty,
      ].includes(firstItem.department);

    return mergedItems.reduce<Record<string, LineItemDetail[]>>(
      (group, item) => {
        if (!group[item.serviceTitle]) {
          group[item.serviceTitle] = [];
        }

        if (shouldCombineItemValue) {
          const sameItemIndex = group[item.serviceTitle].findIndex(
            ({ packageDetailId }) => item.packageDetailId === packageDetailId,
          );

          if (sameItemIndex > -1) {
            group[item.serviceTitle][sameItemIndex] = {
              ...group[item.serviceTitle][sameItemIndex],
              billingValue:
                group[item.serviceTitle][sameItemIndex].billingValue +
                item.billingValue,
              unitsNumber:
                group[item.serviceTitle][sameItemIndex].unitsNumber +
                item.unitsNumber,
            };
          } else {
            group[item.serviceTitle].push(item);
          }
        } else {
          group[item.serviceTitle].push(item);
        }

        return group;
      },
      {},
    );
  }, [mergedItems]);

  return (
    <View>
      {Object.entries(lineItemsGroup).map(([serviceTypeTitle, lineItems]) => (
        <View key={serviceTypeTitle}>
          <ServiceTitle value={serviceTypeTitle} />
          {lineItems.map((item, index) => (
            <ServiceItemList
              key={item.packageDetailId + index}
              label={item.description}
              units={
                item.serviceType === PackageServiceTypeEnum.DeepCleaning
                  ? ""
                  : `${item.unitsNumber || ""} ${item.unitsLabel}`
              }
              price={item.billingValue}
              showPrice={withPrice}
              showUnit={
                item.serviceType === PackageServiceTypeEnum.DeepCleaning
                  ? false
                  : withUnit
              }
            />
          ))}
        </View>
      ))}
    </View>
  );
}

export function ServiceTitle({ value }: { value: string }) {
  return (
    <View className="flex flex-row items-start p-1">
      <View className="mr-3 mt-1.5 w-3">
        <LuceSparkle className="size-3 text-brand-primary" />
      </View>
      <Typography variant="body-sm" className="flex-1">
        {value}
      </Typography>
    </View>
  );
}

export function ServiceItemList({
  label,
  units,
  price,
  showPrice = true,
  showUnit = true,
}: {
  label: string;
  units: string;
  price: number;
  showPrice?: boolean;
  showUnit?: boolean;
}) {
  return (
    <View className="ml-[9px] flex flex-row items-start gap-1 border-l border-border-high pl-[18px]">
      <Typography
        variant="body-sm"
        className="flex-1"
        color="foreground-intermediate"
      >
        {label}
      </Typography>
      <View className="flex flex-row items-center gap-1">
        {showUnit && <Typography variant="body-sm">{units}</Typography>}
        {showPrice && (
          <Typography variant="body-sm">
            {showUnit ? `(${formatPrice(price)})` : formatPrice(price)}
          </Typography>
        )}
      </View>
    </View>
  );
}
